import React, { useEffect, useState } from "react";
import api from "../api/config";
import { GetAllClients, GetClientAccess } from "../api/Client";
import TableWithPagination from "../components/shared/TableWithPagination";
import { Box, Button } from "@mui/material";
import { useNotification } from "../context/NotificationContext";
import AddClientModal from "../components/shared/AddClientModal";
import ClientAccessModal from "../components/shared/ClientAccessModal";
import { GetClientLocation } from "../api/Patient";
import ClientLocationModal from "../components/shared/ClientLocationModal";

const ClientManagement = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openEditClientModal, setOpenEditClientModal] = useState(false);
  const [openClientAccessModal, setOpenClientAccessModal] = useState(false);
  const [openClientLocationModal, setOpenClientLocationModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientAccess, setClientAccess] = useState([]);
  const [clientLocation, setClientLocation] = useState([]);
  const { showNotification } = useNotification();

  const columns = [
    { id: "npi_name", label: "Name" },
    { id: "phone", label: "Phone" },
    { id: "website", label: "Website" },
    { id: "fax", label: "Fax" },
    { id: "address", label: "Address" },
    {
      id: "actions",
      label: "Actions",
      buttons: [
        { label: "Edit Client", id: "edit" },
        { label: "Access", id: "access" },
        { label: "Location", id: "location" },
      ],
    },
  ];

  const fetchClients = async () => {
    setLoading(true);
    try {
      const offset = page * limit;
      const response = await api.get(GetAllClients, {
        params: { offset, limit },
      });
      const transformedData = response.data.clients.map((item) => ({
        ...item,
        id: item.uuid,
      }));
      const { total_count } = response.data;
      setTableData(transformedData);
      setTotalRecords(total_count);
    } catch (error) {
      console.error("Error fetching clients:", error);
      showNotification("Failed to fetch clients", "error");
    } finally {
      setLoading(false);
    }
  };

  const getClientAccess = async (clientUUID) => {
    setLoading(true);
    try {
      const response = await api.get(`${GetClientAccess}/${clientUUID}`);
      console.log("Client access response:", response);
      setClientAccess(response.data);
    } catch (error) {
      console.error("Error fetching client access:", error);
    } finally {
      setLoading(false);
    }
  };

  const getClientLocation = async (clientUUID) => {
    setLoading(true);
    try {
      const response = await api.get(
        `${GetClientLocation}?ClientUID=${clientUUID}`
      );
      const transformedData = response.data.map((item) => ({
        ...item,
        id: item.uuid,
      }));
      console.log("Client location response:", transformedData);
      setClientLocation(transformedData);
    } catch (error) {
      console.error("Error fetching client location:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, limit]);

  const handlePageChange = (newPage) => setPage(newPage);
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(0);
  };

  const handleActionClick = async (actionType, row) => {
    console.log("Action clicked:", actionType, row);
    if (actionType === "edit") {
      setSelectedClient(row);
      setOpenEditClientModal(true);
    } else if (actionType === "access") {
      console.log("Access action clicked", row);
      setSelectedClient(row);
      setOpenClientAccessModal(true);
      await getClientAccess(row.uuid);
    } else if (actionType === "location") {
      setSelectedClient(row);
      setOpenClientLocationModal(true);
      await getClientLocation(row.uuid);
      console.log("Location action clicked");
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100vw", overflowX: "hidden" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSelectedClient(null);
            setOpenEditClientModal(true);
          }}
        >
          Add Client
        </Button>
      </Box>

      <TableWithPagination
        title="Client Management"
        columns={columns}
        rows={tableData}
        isLoading={loading}
        onRefresh={fetchClients}
        totalRecords={totalRecords}
        page={page}
        limit={limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onActionClick={handleActionClick}
        noSearch={true}
      />

      <AddClientModal
        open={openEditClientModal}
        onClose={() => {
          setOpenEditClientModal(false);
        }}
        isEdit={!!selectedClient}
        selectedClient={selectedClient}
        onSaveSuccess={() => {
          fetchClients();
          setOpenEditClientModal(false);
          showNotification(
            selectedClient
              ? "Client updated successfully"
              : "Client added successfully",
            "success"
          );
        }}
      />
      <ClientAccessModal
        open={openClientAccessModal}
        onClose={() => setOpenClientAccessModal(false)}
        isEdit={!!selectedClient}
        selectedClient={selectedClient}
        onSaveSuccess={() => {
          fetchClients();
          setOpenClientAccessModal(false);
          showNotification(
            selectedClient
              ? "Client accesss updated successfully"
              : "Client added successfully",
            "success"
          );
        }}
        selectedAccessLevel={clientAccess.permissions}
        modalLoading={loading}
      />
      <ClientLocationModal
        open={openClientLocationModal}
        onClose={() => setOpenClientLocationModal(false)}
        location={selectedClient}
        locations={clientLocation}
        onSaveSuccess={() => {
          fetchClients();
          setOpenClientLocationModal(false);
          showNotification(
            selectedClient
              ? "Client accesss updated successfully"
              : "Client added successfully",
            "success"
          );
        }}
        modalLoading={loading}
      />
    </Box>
  );
};

export default ClientManagement;
